import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const sections = [
  {
    num: "1",
    title: "Interpretation",
    content: `In these Terms and Conditions: "Company" means Ensure Windows. "Customer" means the person or organisation placing an order. "Contract" means the agreement between the Company and the Customer for the supply of goods and/or services. "Goods" means windows, doors, conservatories, roof lanterns, and related products supplied by the Company. "Services" means the survey, installation, and aftercare services provided by the Company.`,
  },
  {
    num: "2",
    title: "Formation of Contract",
    content: `A Contract is formed when the Customer signs the Company's order form or accepts a written quotation in writing, and the Company issues a written confirmation of order. No Contract shall be binding until the deposit has been received and a written order confirmation issued by the Company. Quotations are valid for 30 days from the date of issue unless otherwise stated.`,
  },
  {
    num: "3",
    title: "Price and Payment",
    content: `The price for Goods and Services shall be as set out in the quotation. Unless otherwise agreed in writing, a deposit of 50% of the Contract value is required upon signing. The balance is due upon completion of installation. The Company reserves the right to vary prices to reflect any increases in material or labour costs that occur after the Contract is formed, subject to providing written notice to the Customer. All prices are inclusive of VAT at the prevailing rate.`,
  },
  {
    num: "4",
    title: "Survey and Specification",
    content: `The Company will carry out a technical survey prior to manufacture. Any variations required following the survey that affect the Contract price will be notified in writing to the Customer for approval before proceeding. The Customer is responsible for ensuring accurate access and measurements are available at the time of survey.`,
  },
  {
    num: "5",
    title: "Delivery and Installation",
    content: `Delivery and installation dates are estimates only and time shall not be of the essence. The Company shall not be liable for any delay caused by circumstances beyond its reasonable control. The Customer must ensure the installation area is clear and accessible on the agreed installation date. Failure to provide access may result in an abortive call-out charge.`,
  },
  {
    num: "6",
    title: "Title and Risk",
    content: `Title in Goods shall remain with the Company until full payment has been received. Risk in the Goods passes to the Customer upon delivery to site. The Customer is responsible for adequate insurance cover from the point of delivery.`,
  },
  {
    num: "7",
    title: "Warranty and Guarantee",
    content: `The Company provides a 10-year guarantee on all uPVC and aluminium frames against manufacturing defects. Glass units carry a 5-year seal failure guarantee. Hardware and mechanisms carry a 2-year guarantee. Guarantees do not cover damage caused by misuse, accidental damage, Acts of God, or failure to carry out reasonable maintenance. Guarantees are not transferable unless agreed in writing.`,
  },
  {
    num: "8",
    title: "Defects and Complaints",
    content: `Any defects in Goods or Services must be reported to the Company in writing within 14 days of installation. The Company will inspect and, where a valid defect is confirmed, will repair or replace at its discretion at no additional cost. Minor aesthetic variations that do not affect performance do not constitute defects.`,
  },
  {
    num: "9",
    title: "Liability",
    content: `The Company's liability under this Contract shall not exceed the total value of the Contract. The Company shall not be liable for any indirect, consequential, or special loss including loss of profit, loss of use, or damage to property not directly caused by the Company. Nothing in these Terms limits liability for death or personal injury caused by negligence, or fraud or fraudulent misrepresentation.`,
  },
  {
    num: "10",
    title: "Cancellation",
    content: `Please refer to our Cancellation Policy for full details of your right to cancel and any applicable cancellation charges.`,
    link: { text: "View Cancellation Policy", to: "/cancellation-policy" },
  },
  {
    num: "11",
    title: "Consumer Rights",
    content: `Nothing in these Terms affects your statutory rights under the Consumer Rights Act 2015 or any other applicable legislation. Domestic customers purchasing goods for personal use retain all rights afforded by consumer protection legislation.`,
  },
  {
    num: "12",
    title: "Data Protection",
    content: `The Company processes personal data in accordance with UK GDPR and the Data Protection Act 2018. Please refer to our Privacy Policy for full details of how we collect, use, and protect your data.`,
    link: { text: "View Privacy Policy", to: "/privacy-policy" },
  },
  {
    num: "13",
    title: "Force Majeure",
    content: `Whilst the Company undertakes to do its utmost to execute every order within the specified period, the Company does not take responsibility for any default due to any act of God, war, strike, lockout, industrial action, fire, flood, drought, tempest or other event beyond the reasonable control of the Company.`,
  },
  {
    num: "14",
    title: "Governing Law",
    content: `These Terms and Conditions shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.`,
  },
  {
    num: "15",
    title: "Severability",
    content: `If any provision of these Terms is found by any court, tribunal or administrative body of competent jurisdiction to be wholly or partly illegal, invalid, void, voidable, unenforceable or unreasonable, the remaining provisions of these Terms and the remainder of such provisions shall continue in full force and effect.`,
  },
];

export default function TermsConditions() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Raleway', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="bg-[#1C2B1F] pt-36 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block text-amber-400 text-xs font-bold uppercase tracking-widest mb-3">Legal</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Terms &amp; Conditions</h1>
          <p className="text-stone-300 text-base">Last updated: 15 April 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">

          <p className="text-zinc-600 text-sm bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md mb-10">
            Please read these Terms and Conditions carefully before placing an order with Ensure Windows. By signing an order form or accepting a quotation, you agree to be bound by these Terms and Conditions.
          </p>

          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.num} className="border-b border-stone-100 pb-8 last:border-0">
                <h2 className="text-lg font-bold text-zinc-900 mb-3 flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold flex-shrink-0">{section.num}</span>
                  {section.title}
                </h2>
                <p className="text-zinc-600 text-sm leading-relaxed ml-11">{section.content}</p>
                {section.link && (
                  <div className="ml-11 mt-3">
                    <Link to={section.link.to} className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1 cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-xs"></i></div>
                      {section.link.text}
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact */}
          <div className="bg-[#1C2B1F] text-white rounded-xl p-6 mt-12">
            <h3 className="font-bold text-lg mb-2">16. Contact</h3>
            <p className="text-stone-300 text-sm mb-4">For any questions regarding these Terms and Conditions, please contact us:</p>
            <address className="not-italic text-stone-300 text-sm space-y-1">
              <p className="font-semibold text-white">Ensure Windows</p>
              <p>71-75 Shelton Street, London WC2H 9JQ</p>
              <a href="mailto:sales@ensurewindows.co.uk" className="text-amber-400 hover:text-amber-300 flex items-center gap-2 cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-line text-xs"></i></div>
                sales@ensurewindows.co.uk
              </a>
              <a href="tel:07852636300" className="text-amber-400 hover:text-amber-300 flex items-center gap-2 cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-xs"></i></div>
                Tel: 07852 636 300
              </a>
            </address>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
